import React, { useState, useEffect } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Grid,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { isValid } from 'swedish-personal-identity-number-validator';
import { useTranslation } from 'react-i18next';
import { addNewUser } from '../../store/actions/general';
import { exportButtonStyle } from '../PatientDetails/PatientDetails';
import { Profession, professions } from '../../lib/interfaces';
import { GENERAL_FONT } from '../../constants/theme';
import FlashSnackbar from '../common/FlashSnackbar';

import type { AppDispatch } from '../../store';

export interface SimpleDialogProps {
  onClose?: () => void;
  modalOpen: boolean;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  p: 4,
  borderRadius: '5px',
};

const AddAdmin = (props: SimpleDialogProps) => {
  const { onClose, modalOpen } = props;

  const [modalOpened, setModalOpened] = useState(false);
  const [flashOpen, setFlashOpen] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const { t } = useTranslation();

  useEffect(() => {
    setModalOpened(modalOpen);
  }, [modalOpen]);

  const handleModalClose = () => {
    setModalOpened(false);
    onClose?.();
    setFormData(emptyForm);
    setErrors(emptyErrorsObject);
  };

  const emptyForm = {
    firstName: '',
    lastName: '',
    personalNumber: '',
    phoneNumber: '',
    email: '',
    profession: '',
  };

  const emptyErrorsObject = {
    firstName: false,
    lastName: false,
    personalNumber: false,
    phoneNumber: false,
    email: false,
    profession: false,
  };

  const [formData, setFormData] = useState(emptyForm);

  const [errors, setErrors] = useState(emptyErrorsObject);

  const [errorFlashOpen, setErrorFlashOpen] = useState(false);

  const getValidatedFieldHelperText = (field: 'personalNumber' | 'email') => {
    if (!errors[field]) return undefined;
    if (formData[field] === '') return `${t(field)} ${t('isRequired')}`;
    return `${t(field)} ${t('invalidFormat')}`;
  };

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({
        ...formData,
        [field]: event.target.value,
      });
      checkErrors(field, event.target.value);
    };

  const checkAllErrors = () => {
    const newErrors = {
      firstName: formData.firstName === '',
      lastName: formData.lastName === '',
      personalNumber:
        formData.personalNumber === '' || !isValid(formData.personalNumber),
      phoneNumber: formData.phoneNumber === '',
      email: formData.email === '' || !isValidEmail(formData.email),
      profession: formData.profession === '',
    };
    setErrors(newErrors);
    return newErrors;
  };

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

  const checkErrors = (field: string, value: string) => {
    if (field === 'email') {
      setErrors({ ...errors, email: !isValidEmail(value) || value === '' });
    } else if (field === 'profession') {
      setErrors({ ...errors, profession: value === '' });
    } else {
      setErrors({
        ...errors,
        [field]:
          field === 'personalNumber'
            ? value === '' || !isValid(value)
            : value === '',
      });
    }
  };

  const handleSubmit = async () => {
    const anyErrors = checkAllErrors();
    if (!Object.values(anyErrors).some((error) => error)) {
      try {
        await dispatch(
          addNewUser({
            firstName: formData.firstName,
            lastName: formData.lastName,
            personalNumber: formData.personalNumber,
            phoneNumber: formData.phoneNumber,
            email: formData.email,
            isAdmin: true,
            profession: formData.profession as Profession,
          })
        );

        setFlashOpen(true);
        setModalOpened(false);
        setFormData(emptyForm);
        onClose?.();
      } catch {
        setErrorFlashOpen(true);
      }
    }
  };

  return (
    <>
      <Modal
        open={modalOpened}
        onClose={handleModalClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={modalOpened}>
          <Box sx={modalStyle}>
            <Typography
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                fontFamily: GENERAL_FONT,
              }}
            >
              {t('addNewAdmin')}
            </Typography>
            <div>
              <Grid container columnSpacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={t('personalNumber')}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={formData.personalNumber}
                    onChange={handleChange('personalNumber')}
                    error={errors.personalNumber}
                    helperText={getValidatedFieldHelperText('personalNumber')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t('profession')}
                    variant="outlined"
                    fullWidth
                    select
                    margin="normal"
                    value={formData.profession}
                    onChange={handleChange('profession')}
                    error={errors.profession}
                    helperText={
                      errors.profession &&
                      `${t('profession')} ${t('isRequired')}`
                    }
                  >
                    {professions.map((profession) => (
                      <MenuItem key={profession} value={profession}>
                        {t(profession)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t('firstName')}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={formData.firstName}
                    onChange={handleChange('firstName')}
                    error={errors.firstName}
                    helperText={
                      errors.firstName && `${t('firstName')} ${t('isRequired')}`
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t('lastName')}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={formData.lastName}
                    onChange={handleChange('lastName')}
                    error={errors.lastName}
                    helperText={
                      errors.lastName && `${t('lastName')} ${t('isRequired')}`
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label={t('email')}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={formData.email}
                    onChange={handleChange('email')}
                    error={errors.email}
                    helperText={getValidatedFieldHelperText('email')}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label={t('phone')}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    value={formData.phoneNumber}
                    onChange={handleChange('phoneNumber')}
                    error={errors.phoneNumber}
                    helperText={
                      errors.phoneNumber && `${t('phone')} ${t('isRequired')}`
                    }
                  />
                </Grid>
              </Grid>
              <Button
                onClick={handleSubmit}
                variant="contained"
                style={{
                  ...exportButtonStyle,
                  float: 'right',
                  marginTop: '20px',
                  marginBottom: '0px',
                }}
                sx={{ textTransform: 'none' }}
              >
                {t('create')}
              </Button>
            </div>
          </Box>
        </Fade>
      </Modal>
      <FlashSnackbar
        open={flashOpen}
        msg={t('adminAddedSuccessfully')}
        severity="success"
        onClose={() => setFlashOpen(false)}
      />
      <FlashSnackbar
        open={errorFlashOpen}
        msg={t('adminAddFailed')}
        severity="error"
        onClose={() => setErrorFlashOpen(false)}
      />
    </>
  );
};

export default AddAdmin;
